import React, { useState } from "react";
import { Reveal } from "../hooks/useReveal";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import { User, Car, CheckCircle2, ShieldCheck, Wallet, Clock, Star, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { LAUNCH_LABEL } from "../mock";
import { registerUser } from "../lib/api";
import Countdown from "./Countdown";

const CITIES = ["Lagos", "Abuja"];
const VEHICLES = ["Saloon car", "SUV / MPV", "Motorbike"];
const MIN_PHONE_DIGITS = 10;
const NIN_LENGTH = 11;

function Field({ label, children }) {
  return (
    <div className="space-y-2">
      <Label className="text-white/80 text-sm">{label}</Label>
      {children}
    </div>
  );
}

const inputCls =
  "h-12 bg-[#0a0e0c] border-white/15 text-white placeholder:text-white/40 focus-visible:ring-[#2fd645]";

// shared validation
function validateCommon(f) {
  if (!f.name.trim()) return "Please enter your full name.";
  if (f.phone.replace(/\D/g, "").length < MIN_PHONE_DIGITS) return "Please enter a valid phone number.";
  if (!new RegExp(`^\\d{${NIN_LENGTH}}$`).test(f.nin.replace(/\s/g, ""))) return `NIN must be exactly ${NIN_LENGTH} digits.`;
  return null;
}

function Success({ name, role, city }) {
  return (
    <div className="text-center py-10">
      <div className="mx-auto h-16 w-16 rounded-full bg-[#2fd645]/15 border border-[#2fd645]/40 flex items-center justify-center">
        <CheckCircle2 className="h-9 w-9 text-[#2fd645]" />
      </div>
      <h3 className="mt-5 font-display font-extrabold text-2xl text-white">Registration confirmed!</h3>
      <p className="mt-2 text-white/60 max-w-sm mx-auto">
        Thanks {name.split(" ")[0]}! You&apos;re registered as a {role}{city ? ` in ${city}` : ""}. We&apos;ll
        contact you on your phone before we launch on {LAUNCH_LABEL}.
      </p>
    </div>
  );
}

function RiderForm() {
  const [f, setF] = useState({ name: "", phone: "", whatsapp: "", email: "", nin: "", city: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });

  const submit = async (e) => {
    e.preventDefault();
    const err = validateCommon(f);
    if (err) return toast.error(err);
    if (f.email && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(f.email)) return toast.error("Please enter a valid email address.");
    if (!f.city) return toast.error("Please choose your city.");
    setLoading(true);
    try {
      await registerUser({ role: "rider", ...f });
      const list = JSON.parse(localStorage.getItem("id_riders") || "[]");
      list.push({ ...f, at: new Date().toISOString() });
      localStorage.setItem("id_riders", JSON.stringify(list));
      setDone(true);
      toast.success("You're registered as a rider!", { description: f.email ? "Check your email for a welcome message." : "See you at launch!" });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (done) return <Success name={f.name} role="rider" city={f.city} />;

  return (
    <form onSubmit={submit} className="space-y-5">
      <Field label="Full name">
        <Input className={inputCls} placeholder="Ada Obi" value={f.name} onChange={set("name")} />
      </Field>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Phone number">
          <Input className={inputCls} placeholder="080 0000 0000" value={f.phone} onChange={set("phone")} />
        </Field>
        <Field label="WhatsApp number (optional)">
          <Input className={inputCls} placeholder="WhatsApp for updates" value={f.whatsapp} onChange={set("whatsapp")} />
        </Field>
      </div>
      <Field label="Email address (optional)">
        <Input className={inputCls} type="email" placeholder="you@example.com" value={f.email} onChange={set("email")} />
      </Field>
      <Field label="NIN (National Identification Number)">
        <Input
          className={inputCls}
          placeholder="11-digit NIN"
          inputMode="numeric"
          maxLength={NIN_LENGTH}
          value={f.nin}
          onChange={(e) => setF({ ...f, nin: e.target.value.replace(/\D/g, "").slice(0, NIN_LENGTH) })}
        />
      </Field>
      <Field label="City">
        <Select value={f.city} onValueChange={set("city")}>
          <SelectTrigger className={inputCls}>
            <SelectValue placeholder="Select your city" />
          </SelectTrigger>
          <SelectContent className="bg-[#0e1311] border-white/15 text-white">
            {CITIES.map((c) => (
              <SelectItem key={c} value={c} className="focus:bg-[#2fd645]/15 focus:text-white">{c}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Field>
      <Button type="submit" disabled={loading} className="w-full h-12 bg-[#2fd645] text-[#06210b] font-bold hover:bg-[#46ff5c] rounded-xl disabled:opacity-70">
        {loading ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Registering…</>) : "Register as a rider"}
      </Button>
      <p className="text-xs text-white/40 text-center">
        Your NIN is used only to verify your identity. By registering you agree to our Terms &amp; Privacy Policy.
      </p>
    </form>
  );
}

function DriverForm() {
  const [f, setF] = useState({ name: "", phone: "", whatsapp: "", email: "", nin: "", city: "", vehicle: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });

  const submit = async (e) => {
    e.preventDefault();
    const err = validateCommon(f);
    if (err) return toast.error(err);
    if (f.email && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(f.email)) return toast.error("Please enter a valid email address.");
    if (!f.city) return toast.error("Please choose your city.");
    if (!f.vehicle) return toast.error("Please select your vehicle type.");
    setLoading(true);
    try {
      await registerUser({ role: "driver", ...f });
      const list = JSON.parse(localStorage.getItem("id_drivers") || "[]");
      list.push({ ...f, at: new Date().toISOString() });
      localStorage.setItem("id_drivers", JSON.stringify(list));
      setDone(true);
      toast.success("You're registered as a driver!", { description: f.email ? "Check your email for a welcome message." : "We'll reach out with verification steps." });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (done) return <Success name={f.name} role="driver" city={f.city} />;

  return (
    <form onSubmit={submit} className="space-y-5">
      <Field label="Full name">
        <Input className={inputCls} placeholder="Musa Bello" value={f.name} onChange={set("name")} />
      </Field>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Phone number">
          <Input className={inputCls} placeholder="080 0000 0000" value={f.phone} onChange={set("phone")} />
        </Field>
        <Field label="WhatsApp number (optional)">
          <Input className={inputCls} placeholder="WhatsApp for updates" value={f.whatsapp} onChange={set("whatsapp")} />
        </Field>
      </div>
      <Field label="Email address (optional)">
        <Input className={inputCls} type="email" placeholder="you@example.com" value={f.email} onChange={set("email")} />
      </Field>
      <Field label="NIN (National Identification Number)">
        <Input
          className={inputCls}
          placeholder="11-digit NIN"
          inputMode="numeric"
          maxLength={NIN_LENGTH}
          value={f.nin}
          onChange={(e) => setF({ ...f, nin: e.target.value.replace(/\D/g, "").slice(0, NIN_LENGTH) })}
        />
      </Field>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="City">
          <Select value={f.city} onValueChange={set("city")}>
            <SelectTrigger className={inputCls}>
              <SelectValue placeholder="Select city" />
            </SelectTrigger>
            <SelectContent className="bg-[#0e1311] border-white/15 text-white">
              {CITIES.map((c) => (
                <SelectItem key={c} value={c} className="focus:bg-[#2fd645]/15 focus:text-white">{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Vehicle type">
          <Select value={f.vehicle} onValueChange={set("vehicle")}>
            <SelectTrigger className={inputCls}>
              <SelectValue placeholder="Select vehicle" />
            </SelectTrigger>
            <SelectContent className="bg-[#0e1311] border-white/15 text-white">
              {VEHICLES.map((v) => (
                <SelectItem key={v} value={v} className="focus:bg-[#2fd645]/15 focus:text-white">{v}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
      </div>
      <Button type="submit" disabled={loading} className="w-full h-12 bg-[#2fd645] text-[#06210b] font-bold hover:bg-[#46ff5c] rounded-xl disabled:opacity-70">
        {loading ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Registering…</>) : "Register to drive"}
      </Button>
      <p className="text-xs text-white/40 text-center">
        We&apos;ll verify your NIN, licence and vehicle papers before launch on {LAUNCH_LABEL}.
      </p>
    </form>
  );
}

export default function SignUp() {
  const perks = {
    rider: [
      { icon: Wallet, text: "Upfront, cashless fares" },
      { icon: ShieldCheck, text: "Verified drivers & SOS safety" },
      { icon: Clock, text: "Fast pickups in minutes" },
    ],
    driver: [
      { icon: Wallet, text: "Keep 92.5% — only 7.5% commission (before tax)" },
      { icon: Clock, text: "Drive on your own schedule" },
      { icon: Star, text: "Grow ratings, earn more" },
    ],
  };

  return (
    <section id="signup" className="relative py-24 bg-[#0a0e0c] border-y border-white/5">
      <div className="absolute inset-0 id-radial opacity-60" />
      <div className="relative mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
        <Reveal className="text-center max-w-2xl mx-auto">
          <span className="inline-flex items-center gap-2 rounded-full border border-[#2fd645]/30 bg-[#2fd645]/10 px-4 py-1.5">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full rounded-full bg-[#2fd645] opacity-75 animate-ping" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-[#2fd645]" />
            </span>
            <span className="text-xs font-semibold uppercase tracking-widest text-[#2fd645]">Registration open now</span>
          </span>
          <h2 className="mt-4 font-display font-extrabold text-white text-4xl sm:text-5xl">
            Register before we launch
          </h2>
          <p className="mt-4 text-white/60 text-lg">
            Sign up early as a rider or driver with just your name, phone number and NIN. Be
            ready to move the moment InstantDrive goes live on {LAUNCH_LABEL}.
          </p>
          <div className="mt-6 flex justify-center">
            <Countdown />
          </div>
        </Reveal>

        <Reveal delay={100} className="mt-12">
          <Tabs defaultValue="rider" className="w-full">
            <TabsList className="mx-auto flex w-full max-w-md bg-[#0e1311] border border-white/10 rounded-2xl p-1.5 h-auto">
              <TabsTrigger
                value="rider"
                className="flex-1 gap-2 rounded-xl py-3 text-white/70 data-[state=active]:bg-[#2fd645] data-[state=active]:text-[#06210b] data-[state=active]:font-bold"
              >
                <User className="h-4 w-4" /> I&apos;m a Rider
              </TabsTrigger>
              <TabsTrigger
                value="driver"
                className="flex-1 gap-2 rounded-xl py-3 text-white/70 data-[state=active]:bg-[#2fd645] data-[state=active]:text-[#06210b] data-[state=active]:font-bold"
              >
                <Car className="h-4 w-4" /> I want to Drive
              </TabsTrigger>
            </TabsList>

            <div className="mt-8 grid lg:grid-cols-[1.4fr_1fr] gap-8 items-start">
              <div className="rounded-3xl border border-white/10 bg-[#0e1311] p-6 sm:p-8">
                <TabsContent value="rider" className="mt-0"><RiderForm /></TabsContent>
                <TabsContent value="driver" className="mt-0"><DriverForm /></TabsContent>
              </div>

              <div className="rounded-3xl border border-[#2fd645]/25 bg-gradient-to-br from-[#0f1613] to-[#0b0f0d] p-6 sm:p-8">
                <TabsContent value="rider" className="mt-0">
                  <h3 className="font-display font-bold text-white text-xl">Why ride with us</h3>
                  <ul className="mt-5 space-y-4">
                    {perks.rider.map((p) => {
                      const Icon = p.icon;
                      return (
                        <li key={p.text} className="flex items-center gap-3 text-white/75">
                          <span className="h-9 w-9 shrink-0 rounded-xl bg-[#2fd645]/12 border border-[#2fd645]/25 flex items-center justify-center">
                            <Icon className="h-4 w-4 text-[#2fd645]" />
                          </span>
                          {p.text}
                        </li>
                      );
                    })}
                  </ul>
                </TabsContent>
                <TabsContent value="driver" className="mt-0">
                  <h3 className="font-display font-bold text-white text-xl">Why drive with us</h3>
                  <ul className="mt-5 space-y-4">
                    {perks.driver.map((p) => {
                      const Icon = p.icon;
                      return (
                        <li key={p.text} className="flex items-center gap-3 text-white/75">
                          <span className="h-9 w-9 shrink-0 rounded-xl bg-[#2fd645]/12 border border-[#2fd645]/25 flex items-center justify-center">
                            <Icon className="h-4 w-4 text-[#2fd645]" />
                          </span>
                          {p.text}
                        </li>
                      );
                    })}
                  </ul>
                </TabsContent>
              </div>
            </div>
          </Tabs>
        </Reveal>
      </div>
    </section>
  );
}
